export interface ChannelSetupPrerequisite {
  conversation_ready: boolean;
  customer_url?: string;
}

export interface ChannelEmailConfig {
  enabled: boolean;
  provider: string;
  from: string;
  reply_to: string;
}

export interface ChannelSmsConfig {
  enabled: boolean;
  sender_id: string;
  provider: string;
  international_provider: string;
  sms_trim_enabled: boolean;
  recipient_name_length: number;
  default_recipient_name: string;
}

export interface ChannelWhatsappConfig {
  enabled: boolean;
  sender_id: string;
  domestic_provider: string;
  international_provider: string;
}

export interface ChannelSetupTemplate {
  sms_body: string;
  email_subject: string;
  email_body: string;
  whatsapp_body: string;
  template_file_id?: string;
  email_template_id?: string;
  template_file_name?: string;
  template_name?: string;
  content_template_id?: string;
  content_template_text?: string;
  whatsapp_template_id?: string;
  whatsapp_template_name?: string;
}

export interface ChannelDltTemplate {
  id: string;
  name: string;
  text: string;
  language?: string;
  rowId?: string;
}

export type CatalogTemplateChannel = 'sms' | 'email' | 'whatsapp';

export type CatalogTemplateUsedFor =
  | 'on_demand_request'
  | 'customer_notifier'
  | 'action_service'
  | 'other';

export interface CatalogTemplateActor {
  manager_id?: string;
  email_address?: string;
  name?: string;
  updated_date?: string | Date | null;
  action?: string;
}

export interface CatalogTemplate {
  id: string;
  channel: CatalogTemplateChannel;
  template_id: string;
  template_name: string;
  template_title?: string;
  template_language: string;
  is_active: boolean;
  last_updated_on?: string | null;
  created_on?: string | Date | null;
  template_used_for?: CatalogTemplateUsedFor;
  created_by?: CatalogTemplateActor | null;
  update_log?: CatalogTemplateActor[];
  account_id?: string;
  template_text?: string;
  sender_id?: string;
  principal_entity_id?: string;
  template_unicode?: boolean;
  email_subject?: string;
  email_from?: string;
  template_language_code?: string;
  template_image?: string;
}

export interface ChannelSetupAttempt {
  attempt: number;
  timing: 'relative' | 'absolute';
  delay_minutes: number;
  absolute_at?: string;
  based_on: 1 | 2;
  channels: Array<'email' | 'sms' | 'whatsapp' | string>;
  templates: ChannelSetupAttemptTemplates;
  default_languages?: {
    sms?: string;
    email?: string;
    whatsapp?: string;
  };
}

export interface ChannelSetupAttemptTemplates {
  sms: Record<string, ChannelSetupTemplate>;
  email: Record<string, ChannelSetupTemplate>;
  whatsapp: Record<string, ChannelSetupTemplate>;
}

export interface ChannelSetupConfig {
  brand_id: string;
  account_id?: string | null;
  notifications_enabled: boolean;
  notify_channel: string;
  communication_channels?: string[];
  date_limit_interval?: number;
  prerequisite: ChannelSetupPrerequisite;
  channels: {
    email: ChannelEmailConfig;
    sms: ChannelSmsConfig;
    whatsapp: ChannelWhatsappConfig;
  };
  attempts: ChannelSetupAttempt[];
  dlt_enabled?: boolean;
  dlt_templates?: ChannelDltTemplate[];
  whatsapp_templates?: ChannelDltTemplate[];
  template_catalogs?: {
    sms?: CatalogTemplate[];
    email?: CatalogTemplate[];
    whatsapp?: CatalogTemplate[];
  };
}

export interface ChannelProviderOption {
  id: string;
  name: string;
  provider_type: string;
  is_available: boolean;
}

export interface ChannelSetupProviders {
  sms: {
    domestic: ChannelProviderOption[];
    international: ChannelProviderOption[];
  };
  email: ChannelProviderOption[];
  whatsapp: {
    domestic: ChannelProviderOption[];
    international: ChannelProviderOption[];
  };
}

export interface ChannelSetupApiResponse<T> {
  status?: number;
  success?: boolean;
  message?: string;
  data?: T;
}

export interface ChannelNotifierTiming {
  start: string;
  end: string;
}

export interface ChannelFilterTiming {
  enable_notifications: boolean;
  duplicate_prevent_delay_hours: number | null;
  single_notification_per_day: boolean;
  expiration_delay_hours: number | null;
  request_count: number | null;
  notification_count: number | null;
  dedup_field_ids: string[];
  notifier_timings: ChannelNotifierTiming[];
  date_limit_interval: number | null;
  is_on_demand_trigger_enabled: boolean;
  on_demand_trigger_ready: boolean;
  defaults?: {
    notifier_timings: ChannelNotifierTiming[];
    date_limit_interval: number | null;
  };
}

export function emptyFilterTiming(): ChannelFilterTiming {
  return {
    enable_notifications: true,
    duplicate_prevent_delay_hours: null,
    single_notification_per_day: true,
    expiration_delay_hours: null,
    request_count: null,
    notification_count: null,
    dedup_field_ids: [],
    notifier_timings: [],
    date_limit_interval: 5,
    is_on_demand_trigger_enabled: false,
    on_demand_trigger_ready: false,
  };
}

export function emptyTemplate(): ChannelSetupTemplate {
  return {
    sms_body: '',
    email_subject: '',
    email_body: '',
    whatsapp_body: '',
    template_file_id: '',
    email_template_id: '',
    template_file_name: '',
    template_name: '',
    content_template_id: '',
    content_template_text: '',
    whatsapp_template_id: '',
    whatsapp_template_name: '',
  };
}

export function emptyChannelTemplates(): ChannelSetupAttemptTemplates {
  return {
    sms: {},
    email: {},
    whatsapp: {},
  };
}

export function emptyAttempt(attempt = 1): ChannelSetupAttempt {
  return {
    attempt,
    timing: 'relative',
    delay_minutes: 0,
    absolute_at: '',
    based_on: attempt === 1 ? 1 : 2,
    channels: [],
    templates: emptyChannelTemplates(),
    default_languages: { sms: '', email: '', whatsapp: '' },
  };
}

export interface ChannelBlacklistFieldOption {
  id: string;
  label: string;
}

export interface ChannelBlacklistConfigSlice {
  is_blacklist_enabled: boolean;
  blacklist_fields: string[];
}

export interface ChannelBlacklistEntry {
  id: string;
  key: string;
  value: string;
  created_date?: string | Date | null;
  created_by?: {
    manager_id?: string;
    email_address?: string;
    name?: string;
  } | null;
  scope?: 'brand' | 'account' | string;
}

export interface ChannelBlacklistSetup extends ChannelBlacklistConfigSlice {
  brand_id: string;
  account_id?: string | null;
  managed_at: 'account' | 'brand';
  can_edit_project: boolean;
  account_enabled?: boolean;
  account_enabled_warning?: string | null;
  brand?: ChannelBlacklistConfigSlice;
  account?: ChannelBlacklistConfigSlice;
  field_options: ChannelBlacklistFieldOption[];
}

export interface ChannelBlacklistEntriesPage {
  managed_at?: 'account' | 'brand';
  scope?: string;
  page: number;
  page_size: number;
  total: number;
  entries: ChannelBlacklistEntry[];
}

export interface ChannelBlacklistImportResult {
  added: number;
  skipped_duplicate: number;
  invalid?: string[];
  total?: number;
}

export function emptyBlacklistSetup(): ChannelBlacklistSetup {
  return {
    brand_id: '',
    account_id: null,
    managed_at: 'brand',
    can_edit_project: true,
    account_enabled: false,
    account_enabled_warning: null,
    is_blacklist_enabled: false,
    blacklist_fields: [],
    brand: { is_blacklist_enabled: false, blacklist_fields: [] },
    account: { is_blacklist_enabled: false, blacklist_fields: [] },
    field_options: [],
  };
}

export interface ChannelUnsubscribeEntry {
  id: string;
  user_id: string;
  brand_id?: string;
  name?: string;
  email_address?: string;
  phone_number?: string;
  customer_id?: string;
  whatsapp_number?: string;
  ip_address?: string;
  source?: string;
  created_date?: string | Date | null;
  created_by?: {
    manager_id?: string;
    email_address?: string;
    name?: string;
  } | null;
}

export interface ChannelUnsubscribeEntriesPage {
  page: number;
  page_size: number;
  has_more?: boolean;
  total?: number | null;
  entries: ChannelUnsubscribeEntry[];
}

export function emptyChannelSetup(brandId = ''): ChannelSetupConfig {
  return {
    brand_id: brandId,
    account_id: null,
    notifications_enabled: true,
    notify_channel: 'none',
    communication_channels: [],
    date_limit_interval: 5,
    prerequisite: { conversation_ready: false, customer_url: '' },
    channels: {
      email: { enabled: false, provider: '', from: '', reply_to: '' },
      sms: {
        enabled: false,
        sender_id: '',
        provider: '',
        international_provider: '',
        sms_trim_enabled: false,
        recipient_name_length: 8,
        default_recipient_name: 'Customer',
      },
      whatsapp: { enabled: false, sender_id: '', domestic_provider: '', international_provider: '' },
    },
    attempts: [],
    dlt_enabled: false,
    dlt_templates: [],
    whatsapp_templates: [],
  };
}
